import type { IFlagsmith } from 'flagsmith';
import { LOCAL_FEATURE_FLAGS, flagsmithConfig } from '@config';
import { webappClient } from './bugsnag';

type User = {
  id: string;
  workspaceId: string;
  organizationId: string;
  isNewUser?: boolean;
  planType?: string;
  environment?: string;
};

export enum FeatureFlagKeys {
  'deviceModeTransformations' = 'device-mode-transformations-web',
  'rs360EnableWht' = 'rs360-enable-wht',
  'text2sql' = 'text-2-sql',
  'mtuUsage' = 'mtu-usage',
  'enableCohortAudience' = 'enable-cohort-audience',
  'enableDataCatalogCustomTypes' = 'enable-data-catalog-custom-types',
  'enabledDataCatalogTPWorkspaceCopy' = 'enable-data-catalog-tp-workspace-copy',
  'reportingDelay' = 'reporting-delay',
  'barGraphForWarehouseDest' = 'bar-graph-for-warehouse-dest',
  'enableDataCatalogVariants' = 'enable-data-catalog-variants',
  'enableRetlCursorColumn' = 'enable-retl-cursor-column',
  'enableDestinationEventDetails' = 'obs-enable-destination-event-details',
  'trackingPlanValidateStageInUTLiveEvents' = 'tracking-plan-validate-stage-ut-live-event',
  'enableNewPermissionsModel' = 'enable-new-permissions-model',
  'enableNewPermissionsModelEnforcement' = 'enable-new-permissions-model-enforcement',
  'enableAccessManagementSelfServeMigration' = 'enable-access-management-self-serve-migration',
  'enableConnectionsPageArrowRenderingThreshold' = 'enable-connections-page-arrow-rendering-threshold',
  'enableRetlFullSync' = 'enable-retl-full-sync',
  'enableBotBlocking' = 'enable-bot-blocking',
  'enableEventBlocking' = 'enable-event-blocking',
  'enableTrackingPlanValidationInSourceLiveEvents' = 'enable-tracking-plan-validation-in-source-live-events',
  'enableDatabricksOAuthForProfiles' = 'enable-profiles-databricks-oauth',
  'enableAudienceV1' = 'enable-audience-v1',
  'enableProfilesCopilot' = 'enable-profiles-copilot',
  'enableProfilesInAppCopilot' = 'enable-profiles-in-app-copilot',
  'enableDataMapper' = 'enable-data-mapper',
  'enableEventUsageAlert' = 'enable-event-usage-alert',
  'enableTransformationDraftMode' = 'enable-transformations-draft-mode',
  'enableETLSunset' = 'enable-etl-sunset',
  'enableFBLASource' = 'enable-fbla-source',
  'enableTransformationsTests' = 'enable-transformations-tests',
  'enableDataGraph' = 'enable-data-graph',
  'enableTransformationsImpactTests' = 'enable-transformations-impact-tests',
  'enableDataGraphAudiences' = 'enable-data-graph-audiences',
  'enableDataGraphAudienceSyncs' = 'enable-data-graph-audience-syncs',
  'enableWorkspaceTokens' = 'enable-workspace-tokens',
  'enableCodeServerTokenExchange' = 'enable-code-server-token-exchange',
}

type FeatureFlagsWithPayload = {
  [K in keyof typeof featureFlagsDefaults]: 'payload' extends keyof (typeof featureFlagsDefaults)[K]
    ? K
    : never;
}[keyof typeof featureFlagsDefaults];

type FeatureFlagPayloads = {
  [K in FeatureFlagsWithPayload]: (typeof featureFlagsDefaults)[K]['payload'];
};

// List of destination feature flags
export enum DestinationFeatureFlags {
  'vdmNext' = 'vdm-next',
}

// ts-prune-ignore-next
export const destinationFeatureFlagKeys = Object.values(DestinationFeatureFlags);
// ts-prune-ignore-next
export const featureFlagKeys = Object.values(FeatureFlagKeys);

const destinationFeatureFlagsDefaults = {
  [DestinationFeatureFlags.vdmNext]: {
    payload: {},
    default: false,
  },
} as const;

// ts-prune-ignore-next
export const featureFlagsDefaults = {
  [FeatureFlagKeys.deviceModeTransformations]: {
    default: false,
  },
  [FeatureFlagKeys.rs360EnableWht]: {
    default: LOCAL_FEATURE_FLAGS.RS360_ENABLE_WHT === 'true',
  },
  [FeatureFlagKeys.text2sql]: {
    default: false,
  },
  [FeatureFlagKeys.mtuUsage]: {
    default: false,
  },
  [FeatureFlagKeys.enableCohortAudience]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_COHORT_AUDIENCE === 'true',
  },
  [FeatureFlagKeys.enableDataCatalogCustomTypes]: {
    default: false,
  },
  [FeatureFlagKeys.enabledDataCatalogTPWorkspaceCopy]: {
    default: false,
  },
  [FeatureFlagKeys.reportingDelay]: {
    payload: {
      delayInMinutes: 0,
    },
    default: false,
  },
  [FeatureFlagKeys.barGraphForWarehouseDest]: {
    default: false,
  },
  [FeatureFlagKeys.enableDataCatalogVariants]: {
    default: false,
  },
  [FeatureFlagKeys.enableRetlCursorColumn]: {
    default: false,
  },
  [FeatureFlagKeys.enableDestinationEventDetails]: {
    default: false,
  },
  [FeatureFlagKeys.trackingPlanValidateStageInUTLiveEvents]: {
    default: false,
  },
  [FeatureFlagKeys.enableNewPermissionsModel]: {
    default: false,
  },
  [FeatureFlagKeys.enableNewPermissionsModelEnforcement]: {
    default: false,
  },
  [FeatureFlagKeys.enableAccessManagementSelfServeMigration]: {
    default: false,
  },
  [FeatureFlagKeys.enableConnectionsPageArrowRenderingThreshold]: {
    default: false,
  },
  [FeatureFlagKeys.enableRetlFullSync]: {
    default: false,
  },
  [FeatureFlagKeys.enableBotBlocking]: {
    default: false,
  },
  [FeatureFlagKeys.enableEventBlocking]: {
    default: false,
  },
  [FeatureFlagKeys.enableDatabricksOAuthForProfiles]: {
    default: false,
  },
  [FeatureFlagKeys.enableTrackingPlanValidationInSourceLiveEvents]: {
    default: false,
  },
  [FeatureFlagKeys.enableAudienceV1]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_AUDIENCES_V1 === 'true',
  },
  [FeatureFlagKeys.enableProfilesCopilot]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_PROFILES_COPILOT === 'true',
  },
  [FeatureFlagKeys.enableProfilesInAppCopilot]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_PROFILES_IN_APP_COPILOT === 'true',
  },
  [FeatureFlagKeys.enableDataMapper]: {
    default: false,
  },
  [FeatureFlagKeys.enableEventUsageAlert]: {
    default: false,
  },
  [FeatureFlagKeys.enableTransformationDraftMode]: {
    default: false,
  },
  [FeatureFlagKeys.enableETLSunset]: {
    default: false,
    payload: {
      etlSunsetDate: '2025-12-01',
      showETLSunsetAlert: false,
      useAllowlistForCreation: false,
    },
  },
  [FeatureFlagKeys.enableFBLASource]: {
    default: false,
  },
  [FeatureFlagKeys.enableTransformationsTests]: {
    default: false,
  },
  [FeatureFlagKeys.enableDataGraph]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_GRAPH === 'true',
  },
  [FeatureFlagKeys.enableTransformationsImpactTests]: {
    default: false,
  },
  [FeatureFlagKeys.enableDataGraphAudiences]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_GRAPH_AUDIENCES === 'true',
  },
  [FeatureFlagKeys.enableDataGraphAudienceSyncs]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_GRAPH_AUDIENCE_SYNCS === 'true',
  },
  [FeatureFlagKeys.enableWorkspaceTokens]: {
    default: false,
  },
  [FeatureFlagKeys.enableCodeServerTokenExchange]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_CODE_SERVER_TOKEN_EXCHANGE === 'true',
  },
} as const;

const flagPrefix = 'AMP_';

class FeatureFlagService {
  hasInitialized = false;

  initializationPromise: Promise<void> | undefined;

  flagsmith: IFlagsmith | undefined;

  private listeners = new Set<() => void>();

  private notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[flagsmith] listener failed', error);
        webappClient.notify(error as Error);
      }
    });
  }

  public subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private isReady(): this is { flagsmith: IFlagsmith } {
    return this.hasInitialized;
  }

  private async initializeFlagsmith() {
    try {
      const flagsmithModule = await import('flagsmith');
      this.flagsmith = flagsmithModule.default;

      await this.flagsmith.init({
        environmentID: flagsmithConfig.environmentKey,
        cacheFlags: true,
        preventFetch: false,
        realtime: true,
        enableAnalytics: true,
        onChange: () => this.notifyListeners(),
      });

      if (this.flagsmith.loadingState!.error) {
        throw new Error('Failed to load Flagsmith');
      }

      this.hasInitialized = true;

      this.notifyListeners();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[flagsmith] failed to initialize:', error);
    }
  }

  constructor() {
    if (flagsmithConfig.environmentKey) {
      this.initializationPromise = this.initializeFlagsmith();
    }
  }

  // Call flagsmith identify when and if the library has been loaded and
  // initialized successfully
  public async identify(user: User) {
    const { workspaceId, organizationId } = user;

    return this.initializationPromise
      ?.then(async () => {
        // At this point, the flagsmith library has been loaded and initialized
        if (!this.isReady()) {
          // eslint-disable-next-line no-console
          console.warn('Flagsmith failed to load, skipping start');
          return;
        }

        await this.flagsmith.identify(workspaceId, { organizationId, workspaceId });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[flagsmith] identify call failed:', error);
      });
  }

  // NOTE:
  // All the following methods are being called after `identify` has been
  // awaited. If the identify call failed, the methods will return the default
  // flag values.

  // Reset the flags if the identify call was successful
  public reset() {
    if (!this.isReady()) {
      // eslint-disable-next-line no-console
      console.warn('Flagsmith failed to load, skipping reset');
      return;
    }

    this.flagsmith.logout();
    this.notifyListeners();
  }

  public isFeatureEnabled(flag: FeatureFlagKeys): boolean {
    if (!this.isReady()) {
      return featureFlagsDefaults[flag].default;
    }

    return this.flagsmith.hasFeature(flag, { fallback: featureFlagsDefaults[flag].default });
  }

  public isDestinationFeatureEnabled(
    destinationType: string,
    flag: DestinationFeatureFlags,
  ): boolean {
    const flagDefault = destinationFeatureFlagsDefaults[flag] ?? { payload: {}, default: false };

    if (!this.isReady()) {
      return (flagDefault.payload as Record<string, boolean>)?.[destinationType] === true;
    }

    const value = this.flagsmith.getValue(flag, {
      json: true,
      fallback: flagDefault.payload,
    }) as Record<string, boolean>;

    return value?.[destinationType] === true;
  }

  public getAllFeatureFlags(): Record<string | FeatureFlagKeys, boolean> {
    if (!this.isReady()) {
      return Object.fromEntries(
        Object.entries(featureFlagsDefaults)
          .filter(([key]) => !destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
          .map(([key, value]) => [`${flagPrefix}${key}`, value.default]),
      );
    }

    return Object.fromEntries(
      Object.entries(this.flagsmith.getAllFlags())
        .filter(([key]) => !destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
        .map(([key, flag]) => [`${flagPrefix}${key}`, flag.enabled]),
    );
  }

  public getAllDestinationFeatureFlags(destinationType: string): Record<string, boolean> {
    if (!this.isReady()) {
      return Object.fromEntries(
        Object.entries(destinationFeatureFlagsDefaults)
          .filter(([key]) => !featureFlagKeys.includes(key as FeatureFlagKeys))
          .map(([key, value]) => [`${flagPrefix}${key}`, value.default]),
      );
    }

    return Object.fromEntries(
      Object.entries(this.flagsmith.getAllFlags())
        .filter(([key]) => !featureFlagKeys.includes(key as FeatureFlagKeys))
        .map(([flagId]) => [
          `${flagPrefix}${flagId}`,
          this.isDestinationFeatureEnabled(destinationType, flagId as DestinationFeatureFlags),
        ]),
    );
  }

  public getFeatureValue<T extends FeatureFlagsWithPayload>(flag: T): FeatureFlagPayloads[T] {
    const flagDefaults = featureFlagsDefaults[flag];
    const fallback = flagDefaults.payload as FeatureFlagPayloads[T];

    if (!this.isReady()) {
      return fallback;
    }

    return this.flagsmith.getValue(flag, { json: true, fallback }) as FeatureFlagPayloads[T];
  }
}

export const experiment = new FeatureFlagService();
